import React, { Component } from 'react'
import { connect } from 'react-redux'
import { Field, Form } from 'react-final-form'
import {
  Button,
  IconButton,
  FormControl,
  MenuItem,
  Typography,
} from '@material-ui/core'
import IconBack from '@material-ui/icons/ArrowBack'
import { injectIntl } from 'react-intl'
import withPromise from 'helpers/withPromise'
import { Select } from 'components/common/FormInputs'
import Input from 'components/common/Form/Input'
import getValidator, { VALIDATOR_IS_EMPTY } from 'validation'
import {
  fetchUserGroupForm,
  setUserGroupInfo,
  clearUserGroupForm,
} from 'reducers/userGroup/reducer'
import Loader from 'components/Loading'
import { siteFetchList } from 'reducers/site/reducer'
import Psfs from 'lib/psfs/Psfs'
import SelectList from 'components/common/SelectList'
import messages from './translate'

class UserGroupForm extends Component {
  constructor(props) {
    super(props)
    this.state = {
      redirect: false,
      name: '',
      users: [],
      sites: [],
      applications: [],
      selectedSites: [],
    }
  }

  componentDidMount() {
    const { fetchUserGroupForm, match, siteFetchList } = this.props
    const psfs = new Psfs()
    psfs.addFields(['id', 'name'])

    siteFetchList({ psfs })
    fetchUserGroupForm({
      entityId: this.getEntityId(match.params),
    })
  }

  shouldComponentUpdate(nextProps, nextState) {
    const {
      history: { goBack },
    } = this.props

    if (nextState.redirect) {
      goBack()

      return false
    }

    return true
  }

  componentDidUpdate(prevProps) {
    const { formData, siteList } = this.props
    const { userGroup } = formData.toJS()

    if (siteList.size && siteList !== prevProps.siteList) {
      const { sites: currentSites } = this.state
      const groupSites = userGroup ? userGroup.sites : []
      const sitesToKeep = currentSites.length ? currentSites : groupSites

      this.setState({
        sites: this.setSiteList(siteList.toArray(), sitesToKeep),
      })
    }

    if (formData !== prevProps.formData && userGroup) {
      const { name, users, sites, applications } = userGroup

      this.setState({
        name,
        users: users.map(user => user.id),
        sites: this.setSiteList(siteList.toArray(), sites),
        applications: applications.map(app => app.id),
      })
    }
  }

  componentWillUnmount() {
    const { clearUserGroupForm } = this.props

    clearUserGroupForm()
  }

  onSubmit = values => {
    const { setUserGroupInfo, match, siteList } = this.props
    const { name, users, sites, applications } = values
    const siteArray = siteList.size === sites.length ? [] : sites

    return withPromise(setUserGroupInfo, {
      entityId: this.getEntityId(match.params),
      values: {
        name,
        users,
        applications,
        sites: siteArray,
      },
    }).then(errors =>
      errors
        ? Promise.resolve(errors)
        : this.setState({
            redirect: true,
          }),
    )
  }

  getEntityId = ({ id }) => (id !== 'create' ? id : false)

  setSiteList = (siteList, sites = []) => {
    const siteArr = sites.length ? sites : siteList

    return siteArr.map(site => site.id || site)
  }

  handleSiteChange = value => {
    this.setState({
      selectedSites: value.filter(site => site).map(site => site.index),
    })
  }

  render() {
    const {
      formData,
      isLoadForm,
      siteList,
      history: { goBack },
      intl: { formatMessage },
    } = this.props
    const { users: userList, applications: appList } = formData.toJS()
    const { name, sites, users, applications, selectedSites } = this.state
    const sitesError =
      selectedSites[0] === null
        ? formatMessage({ id: 'invalid_empty_value' })
        : null

    if (isLoadForm) {
      return <Loader pastDelay />
    }

    return (
      <div className="container-fluid no-breadcrumb">
        <Typography variant="h5" className="headline">
          <IconButton size="medium" className="btn-back" onClick={goBack}>
            <IconBack fontSize="small" />
          </IconButton>
          {formatMessage(messages.userGroupInfo)}
        </Typography>
        <Form
          onSubmit={this.onSubmit}
          initialValues={{ name, sites, users, applications }}
          mutators={{
            onChangeSiteList: ([value], state, { changeValue }) => {
              const siteIds = value.filter(site => site).map(site => site.index)

              changeValue(state, 'sites', () => siteIds)
              this.handleSiteChange(value)
            },
          }}
          render={({
            handleSubmit,
            submitting,
            pristine,
            form: { mutators },
          }) => (
            <form onSubmit={handleSubmit} className="form-basic">
              <Field
                required
                name="name"
                type="text"
                label={formatMessage(messages.name)}
                component={Input}
                disabled={submitting}
                validate={getValidator(VALIDATOR_IS_EMPTY)}
              />
              <FormControl className="form-group" fullWidth>
                <Field
                  multiple
                  name="users"
                  component={Select}
                  label={formatMessage(messages.users)}
                  disabled={submitting}
                  formControlProps={{
                    fullWidth: true,
                    required: true,
                  }}
                  validate={getValidator([VALIDATOR_IS_EMPTY])}
                  inputProps={{
                    multiple: true,
                  }}>
                  {userList.map(item => (
                    <MenuItem button key={`${item.id}`} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Field>
              </FormControl>
              <FormControl fullWidth required className="form-group">
                <Field name="sites" type="hidden" component={() => null} />
                <SelectList
                  multiple
                  required
                  withCheckBox
                  hasAllValueOption
                  outMultipleValues={{
                    all: [],
                    empty: [
                      {
                        index: null,
                        name: '',
                      },
                    ],
                  }}
                  map={{
                    index: 'id',
                    text: 'name',
                  }}
                  variant="outlined"
                  name="sites"
                  disabled={submitting}
                  label={formatMessage(messages.sites)}
                  controlError={sitesError}
                  list={siteList}
                  value={sites}
                  onChange={mutators.onChangeSiteList}
                />
              </FormControl>
              <FormControl className="form-group" fullWidth>
                <Field
                  multiple
                  name="applications"
                  component={Select}
                  label={formatMessage(messages.applications)}
                  disabled={submitting}
                  formControlProps={{ fullWidth: true, required: true }}
                  validate={getValidator([VALIDATOR_IS_EMPTY])}
                  inputProps={{
                    multiple: true,
                  }}>
                  {appList.map(item => (
                    <MenuItem button key={`${item.id}`} value={item.id}>
                      {item.appName}
                    </MenuItem>
                  ))}
                </Field>
              </FormControl>
              <FormControl className="form-group buttons">
                <Button
                  href={null}
                  type="submit"
                  color="primary"
                  size="medium"
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={submitting || pristine || !!sitesError}>
                  {formatMessage(messages.saveInfo)}
                </Button>
              </FormControl>
            </form>
          )}
        />
      </div>
    )
  }
}

export default connect(
  state => ({
    isLoadForm: state.getIn(['userGroup', 'isLoadForm']),
    formData: state.getIn(['userGroup', 'formData']),
    siteList: state.getIn(['site', 'listData']),
  }),
  {
    fetchUserGroupForm,
    setUserGroupInfo,
    clearUserGroupForm,
    siteFetchList,
  },
)(injectIntl(UserGroupForm))
